import { FC, useEffect, useState } from "react";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  logInInitialValues,
  logInValidationSchema,
  LogInValues,
} from "@/shared/schemas/login.schema";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { ButtonLogin } from "@/shared/components/atoms/ButtonLogin";
import { LogIn } from "@/shared/components/organisms/Login";
import { Modal } from "@/shared/components/organisms/Modal";
import { setAccessToken } from "@/app/auth/auth.slice";
import { toasts } from "@/shared/utils/toasts";
import { Profile } from "@/app/auth/auth";

import {
  useLazyProfilesQuery,
  useForgotPasswordMutation,
} from "@/app/auth/auth.api";
import {
  forgotInitialValues,
  forgotValidationSchema,
} from "@/shared/schemas/forgot.schema";

export type LogInPageProps = {};

export const LogInPage: FC<LogInPageProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [triggerProfilesQuery, profilesQuery] = useLazyProfilesQuery();
  const [openModalForgot, setOpenModalForgot] = useState(false);
  const [showProfiles, setShowProfiles] = useState(false);

  const [forgotQuery, forgotResult] = useForgotPasswordMutation();

  const handleSubmit = async (values: LogInValues) => {
    triggerProfilesQuery({
      username: values.email,
      password: values.password,
    });
  };

  const handleSelectProfile = (profile: Profile) => {
    localStorage.setItem("accessToken", profile.access_token);
    dispatch(setAccessToken({ accessToken: profile.access_token }));

    setShowProfiles(false);
  };

  useEffect(() => {
    if (profilesQuery.isSuccess) {
      setShowProfiles(true);
    }
    if (profilesQuery.isError) {
      toasts.danger("Error", "Verifica los datos de acceso");
    }
  }, [profilesQuery]);

  return (
    <>
      <Formik
        initialValues={logInInitialValues}
        onSubmit={handleSubmit}
        validationSchema={logInValidationSchema}
      >
        {({ handleSubmit }) => (
          <LogIn
            isLoading={profilesQuery.isLoading}
            onSubmit={handleSubmit}
            onSelectProfile={handleSelectProfile}
            profiles={profilesQuery?.data}
            showProfiles={showProfiles}
            setShowProfiles={() => setShowProfiles}
            heading={t("auth.title")}
            subheading={t("auth.subtitle")}
            usernameLabelText={t("branch.views.user")}
            usernamePlaceholder={"usuario@email.com"}
            passwordLabelText={t("auth.reset_password_form_password")}
            passwordPlaceholder={t("auth.reset_password_form_password")}
            termsLabelText={t("user.views.forgot")}
            submitButtonText={t("user.views.login")}
            srcImageRight="/logo.png"
            srcLogo=""
            aHeader=""
            hrefHeader=""
            usernameAutoComplete={true}
            passwordAutoComplete={false}
            forgotEvent={() => {
              setOpenModalForgot(true);
              console.log("Datos necesarios para cargar");
            }}
            textForgot={t("user.views.forgot")}
            problemEvent={() => {
              window.open(
                "https://api.whatsapp.com/send?phone=573113803744&text=Hola%2C%20tengo%20problemas%20para%20acceder%20al%20dashboard%20de%20Fitmewise.%20Mi%20email%20es...",
                "_blank"
              );
            }}
            textProlem={t("user.views.problem")}
            buttonText="Ejemplo"
          />
        )}
      </Formik>

      <Formik
        initialValues={forgotInitialValues}
        validationSchema={forgotValidationSchema}
        onSubmit={async (values, formikHelpers) => {
          try {
            forgotQuery(values);
            formikHelpers.resetForm();
            setOpenModalForgot(!openModalForgot);
            toasts.success(
              "Enviado",
              "El correo de recuperación fue enviado exitosamente."
            );
          } catch (error) {
            toasts.danger(
              "Error",
              "No se ha podido enviar el correo de recuperación"
            );
          }
        }}
      >
        {({ handleChange, handleSubmit, errors, values, resetForm }) => (
          <Modal
            open={openModalForgot}
            Close={() => {
              alert("ok");
              setOpenModalForgot(false);
              resetForm();
            }}
            onClose={() => {
              setOpenModalForgot(false);
              resetForm();
            }}
            onClickCreate={handleSubmit}
            textButtonCreate={t("training-wizard.views.continue")}
            textButtonClose={t("general.cancel")}
            tittleModal={t("user.views.forgot")}
            footer={
              <div className="flex justify-end gap-2">
                <ButtonLogin
                  primary
                  text={t("user.views.user_submit")}
                  isLoading={forgotResult.isLoading}
                  role="submit"
                  type="submit"
                  onClick={() => {
                    handleSubmit();
                  }}
                  className="bg-indigo-300 hover:bg-indigo-600"
                />
              </div>
            }
          >
            <div className="gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <div className="col-span-full md:col-span-1 px-4">
                  <InputWithCornerHint
                    label={t("branch.views.email")}
                    name="email"
                    type="email"
                    placeholder={t("branch.views.email")}
                    onChange={handleChange}
                    error={!!errors.email}
                    textError={errors.email}
                    value={values.email}
                  />
                </div>
              </div>
            </div>
          </Modal>
        )}
      </Formik>
    </>
  );
};
